'use client';

import { useEffect, type Dispatch, type SetStateAction } from 'react';
import { User, Phone, FileText, Lock, AlertCircle } from 'lucide-react';
import { useAuth } from '@/app/providers';
import type { ExcursionAsset } from '@/components/booking/UnifiedBookingModal';
import type { ExcursionWizardState } from '@/components/booking/excursion/ExcursionWizard';

/* ── Props ──────────────────────────────────────────────────────────────────── */

interface StepCustomerProps {
  asset: ExcursionAsset;
  state: ExcursionWizardState;
  setState: Dispatch<SetStateAction<ExcursionWizardState>>;
}

/* ── Helpers ─────────────────────────────────────────────────────────────────── */

function fmtDate(dateStr: string): string {
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function buildProfilePhone(prefix: string | null | undefined, number: string | null | undefined): string {
  if (!number) return '';
  if (prefix) return `${prefix} ${number}`.trim();
  return number.trim();
}

/* ── Component ──────────────────────────────────────────────────────────────── */

export default function StepCustomer({ asset, state, setState }: StepCustomerProps) {
  const { user } = useAuth();

  const profilePhone = buildProfilePhone(user?.phone_prefix, user?.phone_number);
  const hasProfilePhone = profilePhone.length > 0;

  /* Inizializza customer.phone col valore di profilo (una sola volta). */
  useEffect(() => {
    if (!hasProfilePhone) return;
    setState((s) => {
      if (s.customer.phone !== undefined) return s;
      return { ...s, customer: { ...s.customer, phone: profilePhone } };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasProfilePhone, profilePhone]);

  /* ── Mini-recap ─────────────────────────────────────────────────────────── */

  const recapParts: string[] = [];
  if (asset.title) recapParts.push(asset.title);
  if (state.date) {
    const datePart = fmtDate(state.date);
    const timePart = state.departureTime ? ` ${state.departureTime}` : '';
    recapParts.push(`${datePart}${timePart}`);
  }
  if (state.participants > 0) {
    recapParts.push(`${state.participants} ${state.participants === 1 ? 'pax' : 'pax'}`);
  }
  const recapLabel = recapParts.join(' · ');

  /* ── Handlers ───────────────────────────────────────────────────────────── */

  const setPhone = (v: string) =>
    setState((s) => ({ ...s, customer: { ...s.customer, phone: v } }));

  const setNotes = (v: string) =>
    setState((s) => ({ ...s, customer: { ...s.customer, notes: v } }));

  /* ── Caso: utente non loggato ────────────────────────────────────────────── */

  if (!user) {
    return (
      <div data-cid="step-customer" className="flex flex-col gap-4 py-6 items-center text-center">
        <AlertCircle size={32} className="text-amber-400" />
        <p className="text-sm font-semibold text-slate-700">
          Accedi per completare la prenotazione.
        </p>
        <p className="text-xs text-slate-400">
          Il login viene gestito prima di aprire il wizard.
        </p>
      </div>
    );
  }

  /* ── Render principale ──────────────────────────────────────────────────── */

  return (
    <div data-cid="step-customer" className="flex flex-col gap-5 py-2">

      {/* Mini-recap contesto */}
      {recapLabel && (
        <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5 text-xs text-slate-500 font-medium leading-snug">
          {recapLabel}
        </div>
      )}

      <p className="text-base font-bold text-slate-800">Dati del cliente</p>

      {/* Nome — read-only */}
      <div className="flex flex-col gap-1">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1">
          <User size={11} />
          Nome
          <Lock size={9} className="text-slate-300 ml-0.5" />
        </span>
        <div className="px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-sm font-semibold text-slate-500 select-none">
          {user.name || '—'}
        </div>
      </div>

      {/* Email — read-only */}
      <div className="flex flex-col gap-1">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1">
          <span className="inline-block w-[11px] h-[11px]">@</span>
          Email
          <Lock size={9} className="text-slate-300 ml-0.5" />
        </span>
        <div className="px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-sm font-semibold text-slate-500 select-none truncate">
          {user.email || '—'}
        </div>
      </div>

      {/* Telefono */}
      <div data-cid="step-customer-phone" className="flex flex-col gap-1">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1">
          <Phone size={11} />
          Telefono
          {!hasProfilePhone && <span className="text-rose-500 ml-0.5">*</span>}
          {hasProfilePhone && <Lock size={9} className="text-slate-300 ml-0.5" />}
        </span>

        {hasProfilePhone ? (
          <>
            <div className="px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-sm font-semibold text-slate-500 select-none">
              {profilePhone}
            </div>
            <p className="text-[11px] text-slate-400 leading-snug">
              Il numero verrà verificato in fase di check-in / KYC.
            </p>
          </>
        ) : (
          <>
            <input
              type="tel"
              placeholder="+39 333 0000000"
              value={state.customer.phone ?? ''}
              onChange={(e) => setPhone(e.target.value)}
              required
              className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-800 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-sky-400/40 focus:border-sky-400 transition-colors"
            />
            <p className="text-[11px] text-slate-400 leading-snug">
              Numero obbligatorio — verrà verificato in fase di check-in / KYC.
            </p>
          </>
        )}
      </div>

      {/* Note — opzionale */}
      <div data-cid="step-customer-notes" className="flex flex-col gap-1">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1">
          <FileText size={11} />
          Note (opzionale)
        </span>
        <textarea
          rows={3}
          placeholder="Richieste particolari, domande all'operatore…"
          value={state.customer.notes ?? ''}
          onChange={(e) => setNotes(e.target.value)}
          className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-800 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-sky-400/40 focus:border-sky-400 transition-colors resize-none"
        />
      </div>

    </div>
  );
}
